import { useState } from "react";
import { useAtom } from "jotai";
import LicensePlate from "../../../../common/LicensePlate/LicensePlate";
import FlotteDetail from "../FlotteDetail/FlotteDetail";
import FlotteSimulator from "../FlotteSimulator/FlotteSimulator";
import CommentaireTimeline from "../FlotteDetail/CommentaireTimeline";
import StatutTimeline from "../FlotteDetail/StatutTimeline";
import PositionTimeline from "../FlotteDetail/PositionTimeline";
import LocalisationTimeline from "../FlotteDetail/LocalisationTimeline";
import SecteurEditor from "../FlotteDetail/SecteurEditor";
import useFlotteDetail from "../FlotteDetail/useFlotteDetail";
import FlotteLigneVO from "./FlotteLigneVO";
import Can from "../../../../common/Can/Can";
import DocumentsPanel from "../../../Documents/components/DocumentsPanel";
import { userAccessEnum } from "../../../../../config/accessEnum";
import { connectedUserAtom } from "../../../../../middlewares/AuthMiddleware";
import { isVORole } from "./FlotteUtils";
import { columnsAtom } from "./useFlotteTable";
import { renderCellByType } from "./Cells/Cells";

const DetailSection = ({ title, children }) => {
  return (
    <div className="mb-4">
      <h6
        className="font-weight-bold"
        style={{
          color: "#2497e9",
        }}
      >
        <i className="fa fa-caret-right mr-2" />
        {title}
      </h6>
      <div>{children}</div>
    </div>
  );
};

const getValueByPath = (obj, path) => {
  if (!path || !obj) return undefined;
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

const RowSummary = ({ item, visibleHeaders }) => {
  const [columns] = useAtom(columnsAtom);

  const visibleKeys = Object.keys(visibleHeaders).filter(
    (key) => visibleHeaders[key] && key !== "actions",
  );

  return (
    <div
      style={{
        overflowX: "auto",
        marginBottom: 16,
        borderBottom: "1px solid #dee2e6",
        paddingBottom: 12,
      }}
    >
      <table
        className="table table-sm table-bordered mb-0"
        style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}
      >
        <thead className="table-light">
          <tr>
            {visibleKeys.map((key) => (
              <th
                key={key}
                style={{
                  fontSize: "0.7rem",
                  padding: "4px 8px",
                  fontWeight: 600,
                  color: "#6c757d",
                }}
              >
                {columns[key]?.title || key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr style={{ backgroundColor: "#e8f4fd" }}>
            {visibleKeys.map((key) => {
              const col = columns[key];
              if (!col) return <td key={key} />;
              const value = getValueByPath(item, col.path);
              return (
                <td key={key} style={{ padding: "4px 8px" }}>
                  {renderCellByType(col.type, value, item)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const FlotteRowDetailModal = ({ item, visibleHeaders }) => {
  const [connectedUser] = useAtom(connectedUserAtom);
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);

  const {
    onAddStatut,
    onAddCommentaire,
    onAddPosition,
    onResetPosition,
    onAddLocalisation,
    onModifierSecteur,
  } = useFlotteDetail({ item });

  const ident = item.identification || {};

  return (
    <div>
      <h5 className="mb-3">
        <i className="fas fa-car mr-2"></i>
        {ident.marque} {ident.modele}
        <LicensePlate
          value={ident.immatriculation}
          size="sm"
          className="ml-2"
        />
        {item.contrat?.intitule_client && (
          <small className="text-muted ml-3">
            {item.contrat.intitule_client}
          </small>
        )}
      </h5>

      {visibleHeaders && (
        <RowSummary item={item} visibleHeaders={visibleHeaders} />
      )}

      {/* Modules réservés aux non-VO */}
      {!isVORole(connectedUser.role) && (
        <DetailSection title="Mouvements">
          <FlotteSimulator item={item} />
        </DetailSection>
      )}

      {/* Modules accessibles à tous (y compris VO) */}
      <div className="row">
        <div className="col-12 col-md-6 col-lg-3">
          <DetailSection title="Historique des statuts">
            <StatutTimeline
              onAddStatut={onAddStatut}
              statutHistorique={item.statut_historique || []}
              vehicleStatut={item.statut}
              item={item}
            />
          </DetailSection>
        </div>
        <div className="col-12 col-md-6 col-lg-3">
          <DetailSection title="Historique des localisations">
            <LocalisationTimeline
              localisationHistorique={item.localisation_historique || []}
              onAddLocalisation={onAddLocalisation}
              vehicleLocalisation={item.localisation}
              vehicleCatalogue={item.catalogue}
            />
          </DetailSection>
        </div>
        <div className="col-12 col-md-6 col-lg-3">
          <DetailSection title="Commentaires">
            <CommentaireTimeline
              commentaireHistorique={item.commentaires}
              onAddCommentaire={onAddCommentaire}
            />
          </DetailSection>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <DetailSection title="Historique des positions">
            <PositionTimeline
              positionHistorique={item.position_historique || []}
              onAddPosition={onAddPosition}
              onResetPosition={onResetPosition}
              vehicleStatut={item.statut}
              vehiclePosition={item.position}
            />
          </DetailSection>
        </div>

        {/* Secteur réservé aux non-VO (notion liée au contrat) */}
        {!isVORole(connectedUser.role) && (
          <div className="col-12 col-md-6 col-lg-3">
            <DetailSection title="Secteur">
              <SecteurEditor
                vehicleSecteur={item.contrat?.type_client}
                onModifierSecteur={onModifierSecteur}
              />
            </DetailSection>
          </div>
        )}

        {/* Informations détaillées réservées aux non-VO */}
        {!isVORole(connectedUser.role) && (
          <div className="col-12 mt-4">
            <DetailSection title="Valeurs VO">
              <FlotteLigneVO item={item} />
            </DetailSection>
          </div>
        )}
      </div>

      {/* LS-693 — documents du véhicule, tous modules confondus, pré-filtrés sur
          son immatriculation. `immatriculations` (liste exacte, normalisée côté
          API) plutôt que `immatriculation` : la recherche partielle ferait
          remonter les documents d'autres véhicules dont l'immat contient
          celle-ci. Réservé aux profils qui ont la permission — les rôles VO
          n'en font pas partie. */}
      {ident.immatriculation && (
        <Can I={userAccessEnum.CONSULTER_DOCUMENTS_VEHICULE}>
          <DetailSection title="Documents">
            <DocumentsPanel
              filtresVerrouilles={{
                immatriculations: [ident.immatriculation],
              }}
              messageVide="Aucun document rattaché à ce véhicule. Les véhicules importés depuis HiTech, sans devis Leasecar, n'ont aucun document amont."
            />
          </DetailSection>
        </Can>
      )}

      {!isVORole(connectedUser.role) && (
        <>
          <div className="mt-4">
            <button
              className="btn btn-link border-0 shadow-none btn-sm"
              onClick={() => setShowDetailedInfo(!showDetailedInfo)}
            >
              <i
                className={`fa ${
                  showDetailedInfo ? "fa-eye-slash" : "fa-eye"
                } mr-2`}
              />
              {showDetailedInfo
                ? "Masquer les informations détaillées"
                : "Afficher les informations détaillées"}
            </button>
          </div>

          {showDetailedInfo && (
            <DetailSection title="Informations détaillées">
              <FlotteDetail item={item} />
            </DetailSection>
          )}
        </>
      )}
    </div>
  );
};

export default FlotteRowDetailModal;
